(function(){
'use strict';

/**
 * Coralogix RUM Service
 *
 * Integrates Coralogix Real User Monitoring with the existing analytics infrastructure.
 * Provides automatic performance monitoring, error tracking, and user experience analytics.
 *
 * Privacy: Only tracks pseudonymized user ID, no email or name data for compliance.
 */
angular.module('classy').service('coralogixRumService', ["$log", "$q", function ($log, $q) {
  this.name = 'coralogixRumService';

  /**
   * Extracts classy session ID from browser cookies
   * @returns {string|undefined} Classy session ID if found, undefined otherwise
   */
  var getClassySessionId = function getClassySessionId() {
    if (typeof document === 'undefined') {
      return undefined;
    }

    try {
      var cookieString = document.cookie.split('; ').find(function (row) {
        return row.startsWith('classy-session-id=');
      });

      if (cookieString) {
        return cookieString.split('=')[1];
      }
    } catch (error) {
      $log.debug('[coralogixRumService] Failed to parse classy-session-id cookie:', error);
    }

    return undefined;
  };

  /**
   * Extracts heap user ID from browser cookies
   * @returns {string|undefined} Heap user ID if found, undefined otherwise
   */
  var getHeapUserId = function getHeapUserId() {
    if (!SC.heapId || typeof document === 'undefined') {
      return undefined;
    }

    try {
      var heapCookieName = '_hp2_id.' + SC.heapId;
      var cookieString = document.cookie.split('; ').find(function (row) {
        return row.startsWith(heapCookieName + '=');
      });

      if (cookieString) {
        var heapCookieValue = cookieString.split('=')[1];
        var heapCookieParsed = JSON.parse(decodeURIComponent(heapCookieValue));

        if (heapCookieParsed && heapCookieParsed.userId) {
          return heapCookieParsed.userId;
        }
      }
    } catch (error) {
      $log.debug('[coralogixRumService] Failed to parse heap cookie:', error);
    }

    return undefined;
  };

  this.init = function () {
    if (typeof window.CoralogixRum !== 'undefined' && SC.coralogixRumKey) {
      try {
        var initConfig = {
          stringifyCustomLogData: true,
          public_key: SC.coralogixRumKey,
          environment: SC.env || 'prod',
          application: SC.coralogixRumApplicationName || 'frs',
          version: SC.appVersion || '1.0.0',
          coralogixDomain: 'US2',
          user_context: {
            user_id: getHeapUserId(),
            user_name: SC.member && SC.member.id ? SC.member.id.toString() : getClassySessionId()
          },
          labels: {
            environment: SC.env || 'prod',
            application: SC.coralogixRumApplicationName || 'frs',
            campaign_id: SC.campaign && SC.campaign.id ? SC.campaign.id.toString() : undefined,
            organization_id: SC.organization && SC.organization.id ? SC.organization.id.toString() : undefined,
            campaign_type: SC.campaign && SC.campaign.type ? SC.campaign.type : undefined
          },
          // Add Traceparent to headers to correlate frontend RUM with backend traces
          traceParentInHeader: {
            enabled: true,
            options: {
              allowedTracingUrls: [/classy-test\.org/, /classy\.org/, /frs-api/, /org-api/, /api/]
            }
          },
          sessionConfig: {
            alwaysTrackSessionsWithErrors: true,
            sessionSampleRate: parseInt(SC.coralogixRumSessionSampleRate, 10) || 100
          },
          ignoreUrls: [
          // Static assets
          /.*\.svg$/, /.*\.png$/, /.*\.jpg$/, /.*\.jpeg$/, /.*\.gif$/, /.*\.ico$/, /.*\.css$/, /.*\.js$/, /.*\.woff$/, /.*\.woff2$/, /.*\.ttf$/, /.*\.eot$/,
          // Analytics and tracking
          /.*analytics.*/, /.*tracking.*/, /.*gtag.*/, /.*facebook.*/, /.*google-analytics.*/,
          // Health checks and monitoring
          /.*healthcheck.*/, /.*ping.*/],
          ignoreErrors: [
          // Common browser errors that aren't actionable
          /Script error/, /Non-Error promise rejection captured/, /ResizeObserver loop limit exceeded/, /ChunkLoadError/, /Loading chunk \d+ failed/,
          // Network errors
          /NetworkError/, /Failed to fetch/,
          // Third-party script errors
          /facebook.com/, /google.com/, /googletagmanager.com/, /doubleclick.net/]
        };

        window.CoralogixRum.init(initConfig);

        $log.info('[coralogixRumService] Initialized successfully');
        $log.debug('[coralogixRumService] Trace configuration:', JSON.stringify(initConfig.traceParentInHeader));

        // Verify configuration was applied
        setTimeout(function () {
          if (window.CoralogixRum && window.CoralogixRum._config) {
            $log.debug('[coralogixRumService] RUM config after init:', window.CoralogixRum._config);
          }
        }, 1000);

        return true;
      } catch (error) {
        $log.error('[coralogixRumService] Initialization failed:', error);
        return false;
      }
    } else {
      $log.warn('[coralogixRumService] Failed to initialize - missing SDK or API key');
      return false;
    }
  };

  // Required interface methods for scAnalytics compatibility
  this.loaded = function () {
    return $q.when(!!window.CoralogixRum);
  };

  this.registerSuperProperties = function () {
    // No-op - RUM handles this automatically through labels
  };

  this.identifyMember = function (id) {
    // Update user context if member data changes
    if (window.CoralogixRum) {
      try {
        var userId = id ? id.toString() : getHeapUserId();
        var userSessionId = id ? id.toString() : getClassySessionId();
        if (userId || userSessionId) {
          window.CoralogixRum.setUserContext({
            user_id: userId,
            user: userSessionId
          });
          $log.debug('[coralogixRumService] Updated user context for member:', userId || userSessionId);
        }
      } catch (error) {
        $log.error('[coralogixRumService] Failed to update user context:', error);
      }
    }
  };

  this.eventBeacon = function () {
    // No-op - RUM handles page unload automatically
  };

  this.recordPageView = function () {
    // No-op - RUM handles page views automatically
    // Could add custom business context here if needed
  };

  this.track = function (event, data) {
    // Only track business-specific events, not performance
    if (window.CoralogixRum && this.isBusinessEvent(event)) {
      try {
        var logData = {
          event_name: event,
          event_data: data,
          campaign_id: SC.campaign && SC.campaign.id ? SC.campaign.id : undefined,
          organization_id: SC.organization && SC.organization.id ? SC.organization.id : undefined,
          user_id: SC.member && SC.member.id ? SC.member.id : getHeapUserId(),
          user: SC.member && SC.member.id ? SC.member.id : getClassySessionId(),
          page_url: window.location.href,
          timestamp: new Date().toISOString()
        };

        // Use the correct Coralogix RUM API: log(severity, message, data, labels)
        var severity = window.CoralogixRum.CoralogixLogSeverity && window.CoralogixRum.CoralogixLogSeverity.Info || 'info';
        window.CoralogixRum.log(severity, 'Business Event: ' + event, logData);
        $log.debug('[coralogixRumService] Tracked business event:', event);
      } catch (error) {
        $log.error('[coralogixRumService] Failed to track event:', error);
      }
    }
  };

  this.isBusinessEvent = function (event) {
    // Only track donation/campaign related events, not performance
    var businessEvents = ['donation_started', 'donation_completed', 'donation_failed', 'campaign_viewed', 'fundraiser_created', 'team_joined', 'share_clicked', 'form_error', 'payment_error', 'checkout_started', 'checkout_completed', 'registration_started', 'registration_completed',
    // Currency-related events
    'currency_change_detected', 'currency_donor_selection', 'currency_url_param', 'currency_init', 'currency_selection_disabled', 'currency_select_init', 'currency_donation_submit', 'donation_checkout_response', 'unset_blacklisted_currency'];
    return businessEvents.includes(event);
  };

  /**
   * Custom log method for trace-level logging (replaces window.newrelic.log)
   * @param {string} eventName - The name of the event
   * @param {object} options - Options object with level and customAttributes
   */
  this.logTrace = function (eventName) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    if (window.CoralogixRum) {
      try {
        var level = options.level || 'debug';
        var logData = Object.assign({
          event_name: eventName,
          campaign_id: SC.campaign && SC.campaign.id ? SC.campaign.id : undefined,
          organization_id: SC.organization && SC.organization.id ? SC.organization.id : undefined,
          user_id: SC.member && SC.member.id ? SC.member.id : getHeapUserId(),
          user: SC.member && SC.member.id ? SC.member.id : getClassySessionId(),
          page_url: window.location.href,
          timestamp: new Date().toISOString()
        }, options.customAttributes || {});

        // Map level to appropriate Coralogix RUM method
        var message = 'Trace Event: ' + eventName;
        if (level === 'error') {
          window.CoralogixRum.error(message, logData);
        } else if (level === 'warn' || level === 'warning') {
          window.CoralogixRum.warning(message, logData);
        } else if (level === 'info') {
          window.CoralogixRum.info(message, logData);
        } else {
          // Default to debug level
          window.CoralogixRum.debug(message, logData);
        }
        $log.debug('[coralogixRumService] Logged trace event:', eventName);
      } catch (error) {
        $log.error('[coralogixRumService] Failed to log trace event:', error);
      }
    }
  };

  /**
   * Helper function to replace window.newrelic.log calls
   * Maintains the same interface as New Relic for easier migration
   * @param {string} eventName - The name of the event
   * @param {object} options - Options object with level and customAttributes (New Relic format)
   */
  this.logEvent = function (eventName) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    // Convert New Relic format to Coralogix format
    this.logTrace(eventName, {
      level: options.level === 'trace' ? 'debug' : options.level || 'info',
      customAttributes: options.customAttributes || options
    });
  };
}]);
})();